/* Zodia weight selector. Loaded only for the explicitly scoped test product.
 * Shopify variants own combinations, availability and prices. No price arithmetic.
 */
(() => {
  async function mount(script) {
    const sectionId = script.dataset.zodiaWeightSection;
    const picker = document.querySelector(`variant-radios[data-section="${sectionId}"], variant-selects[data-section="${sectionId}"]`);
    if (!picker || picker.dataset.zodiaWeightMounted) return;
    await customElements.whenDefined(picker.localName);
    if (picker.dataset.zodiaWeightMounted) return;
    const variants = picker.getVariantData();
    const radios = picker.localName === 'variant-radios';
    const groups = Array.from(picker.querySelectorAll(radios ? 'fieldset' : 'select'));
    if (groups.length !== 2 || !variants.length || variants.some(v => v.options.length !== 2)) return;
    const form = document.getElementById(`product-form-${sectionId}`);
    const idInput = form && form.querySelector('input[name="id"]');
    if (!form || !idInput) return;
    picker.dataset.zodiaWeightMounted = 'true';
    const weights = radios ? Array.from(groups[1].querySelectorAll('input[type="radio"]')) : Array.from(groups[1].options).map(o => o.cloneNode(true));
    weights.sort((a, b) => a.value.localeCompare(b.value, 'ja', { numeric: true }));
    if (radios) {
      weights.forEach(input => {
        const label = Array.from(groups[1].querySelectorAll('label')).find(item => item.htmlFor === input.id);
        groups[1].append(input);
        if (label) groups[1].append(label);
      });
    }
    const labels = radios ? new Map(weights.map(input => [input, Array.from(groups[1].querySelectorAll('label')).find(label => label.htmlFor === input.id)])) : null;
    const baseLabels = new Map(weights.map(input => [input.value, radios ? labels.get(input)?.textContent.trim() || input.value : input.textContent]));
    const notice = document.createElement('p');
    notice.className = 'product__text caption';
    notice.id = `ZodiaWeightStatus-${sectionId}`;
    notice.setAttribute('role', 'status');
    notice.setAttribute('aria-live', 'polite');
    picker.append(notice);
    groups[1].setAttribute('aria-describedby', notice.id);
    let request = 0;
    let controller;
    let readyId = null;
    const originalChange = picker.onVariantChange;
    picker.removeEventListener('change', originalChange);

    function read(index) {
      return radios ? groups[index].querySelector('input:checked')?.value : groups[index].value;
    }
    function syncWeights() {
      const model = read(0);
      const old = read(1);
      const matches = variants.filter(v => v.options[0] === model);
      const existing = matches.find(v => v.options[1] === old);
      const chosen = existing || matches.find(v => v.available) || matches[0];
      const value = chosen?.options[1];
      if (!radios) groups[1].replaceChildren();
      weights.forEach(input => {
        const variant = matches.find(v => v.options[1] === input.value);
        if (radios) {
          const label = labels.get(input);
          input.hidden = !variant;
          input.disabled = !variant;
          input.checked = !!variant && input.value === value;
          if (label) {
            label.hidden = !variant;
            label.style.display = variant ? '' : 'none';
            label.textContent = `${baseLabels.get(input.value)}${variant && !variant.available ? '（在庫切れ）' : ''}`;
          }
        } else if (variant) {
          const option = input.cloneNode(true);
          option.value = input.value;
          option.textContent = `${baseLabels.get(input.value)}${!variant.available ? '（在庫切れ）' : ''}`;
          groups[1].append(option);
        }
      });
      if (!radios) groups[1].value = value || '';
      return old && value && old !== value ? `このモデルに対応する重量 ${value} に切り替えました。` : '';
    }
    function selectedVariant() {
      return variants.find(v => v.options[0] === read(0) && v.options[1] === read(1));
    }
    function guard(event) {
      const selected = selectedVariant();
      if (!selected || !selected.available || readyId !== String(selected.id) || idInput.value !== String(selected.id)) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }
    form.addEventListener('submit', guard, true);
    const addButton = form.querySelector('[name="add"]');
    if (addButton) addButton.addEventListener('click', guard, true);

    picker.updateVariantInput = function () {
      const scope = this.closest('section');
      const forms = scope ? scope.querySelectorAll('form[action*="/cart/add"]') : [form];
      forms.forEach(productForm => {
        const input = productForm.querySelector('input[name="id"]');
        if (!input) return;
        input.value = this.currentVariant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    };
    picker.updatePickupAvailability = async function () {
      const pickup = this.closest('section')?.querySelector('pickup-availability');
      if (!pickup) return;
      const selectedId = this.currentVariant?.id;
      if (typeof pickup.fetchAvailability !== 'function') {
        await customElements.whenDefined('pickup-availability');
      }
      if (!pickup.isConnected || this.currentVariant?.id !== selectedId) return;
      if (typeof pickup.fetchAvailability !== 'function') return;
      if (this.currentVariant?.available) pickup.fetchAvailability(this.currentVariant.id);
      else { pickup.removeAttribute('available'); pickup.innerHTML = ''; }
    };
    // Preserve draft preview keys, theme preview IDs and view while updating selection.
    picker.updateURL = function () {
      if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', this.currentVariant.id);
      history.replaceState({}, '', url);
    };
    picker.renderProductInfo = async function () {
      const variant = this.currentVariant;
      const currentRequest = ++request;
      controller?.abort();
      controller = new AbortController();
      readyId = null;
      document.getElementById(`price-${sectionId}`)?.classList.add('visibility-hidden');
      picker.setAttribute('aria-busy', 'true');
      const url = new URL(window.location.href);
      // Draft preview must retain its key. Normal products use the product URL.
      if (!url.pathname.includes('products_preview')) url.pathname = new URL(this.dataset.url, url).pathname;
      url.searchParams.set('variant', variant.id);
      url.searchParams.set('section_id', sectionId);
      try {
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error('Price section unavailable');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (currentRequest !== request || this.currentVariant?.id !== variant.id) return;
        const source = html.getElementById(`price-${sectionId}`);
        const serverInput = html.getElementById(`product-form-${sectionId}`)?.querySelector('input[name="id"]');
        const destination = document.getElementById(`price-${sectionId}`);
        if (!source || !destination || serverInput?.value !== String(variant.id)) throw new Error('Variant response mismatch');
        destination.innerHTML = source.innerHTML;
        destination.classList.remove('visibility-hidden');
        readyId = String(variant.id);
        this.toggleAddButton(!variant.available, window.variantStrings.soldOut);
      } catch (error) {
        if (error.name === 'AbortError' || currentRequest !== request) return;
        readyId = null;
        this.toggleAddButton(true, '価格を確認できません');
        notice.textContent = '価格を取得できませんでした。重量を選び直すか、ページを再読み込みしてください。';
      } finally {
        if (currentRequest === request) picker.removeAttribute('aria-busy');
      }
    };
    function change(event) {
      if (event && !groups.some(group => group === event.target || group.contains(event.target))) return;
      ++request;
      controller?.abort();
      readyId = null;
      notice.textContent = syncWeights();
      const selected = selectedVariant();
      if (!selected) {
        idInput.value = '';
        picker.currentVariant = null;
        picker.toggleAddButton(true, window.variantStrings.unavailable);
        notice.textContent = '対応する重量を選択してください。';
        picker.removeAttribute('aria-busy');
        return;
      }
      originalChange.call(picker);
    }
    picker.addEventListener('change', change);
    change();
  }
  function init() {
    document.querySelectorAll('script[data-zodia-weight-section]').forEach(script => mount(script));
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
  document.addEventListener('shopify:section:load', init);
})();
